// IndexedDB-backed scanner adapter composed with the live camera controller.
//
// Stage injects the production camera/compute ScanController. This adapter then
// resolves the physical code against the local demo project and persists tags,
// activation state, linked collections and arbitrary save JSON in same-origin
// IndexedDB. It contains no Firebase, network requests, API routes, or external
// persistence.
//
// It is engine-internal: gameplay code must never import it directly. Gameplay
// talks only to the public StickerService, which depends on the
// StickerScannerAdapter port. The owner can still swap in a private production
// adapter without touching gameplay code.
//
// preferredCode remains only as a deterministic isolated-test seam. Bare scans
// always invoke the live controller and never consult developer-selected state.

import type {
  AdapterScanRequest,
  AdapterScanSession,
  StickerScannerAdapter,
} from '../sticker_adapter';
import type { JsonObject, StickerCode, StickerCodeStatus } from '../sticker_types';
import { StickerError } from '../sticker_types';
import { deepCloneJson } from './clone';
import type { ScanController } from './scan_controller';
import {
  DEFAULT_TAG_ID,
  FIXTURE_CODES,
  getAllCodeRecords,
  getAllSaveRecords,
  getAllTagRecords,
  getCodeRecord,
  getMeta,
  getSaveRecord,
  linkNewCodeToExistingSave,
  linkedCodesFrom,
  openStickerDb,
  putCodeRecord,
  putSaveRecord,
  resolveTag,
  seedSaveData,
  setMeta,
  type CodeRecord,
} from './indexeddb_db';

const LAST_COLLECTION_CODE = 'lastCollectionCode';

export interface IndexedDBScannerAdapterOptions {
  /** Live camera/compute controller injected by Stage. */
  controller?: ScanController;
  /** Inject an IDBFactory (e.g. fake-indexeddb) for tests. */
  indexedDB?: IDBFactory;
  /** Optional latency for preferredCode-only tests. Default 0. */
  scanDelayMs?: number;
}

interface ActiveSession {
  code: StickerCode;
  saveId: string;
  isNewCode: boolean;
}

function delay(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new StickerError('cancelled', 'Scan cancelled'));
      return;
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timer);
      reject(new StickerError('cancelled', 'Scan cancelled'));
    };
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

function saveIdForCode(code: StickerCode): string {
  return `save-${code}`;
}

export class IndexedDBScannerAdapter implements StickerScannerAdapter {
  readonly id = 'indexeddb';

  private readonly controller?: ScanController;
  private readonly factory?: IDBFactory;
  private readonly scanDelayMs: number;

  // Session mappings are runtime-only; save data lives in IndexedDB. endSession
  // clears only this map so persistent data is never destroyed by ending a
  // session.
  private sessions = new Map<string, ActiveSession>();
  private sessionCounter = 0;

  constructor(options: IndexedDBScannerAdapterOptions = {}) {
    this.controller = options.controller;
    this.factory = options.indexedDB;
    this.scanDelayMs = options.scanDelayMs ?? 0;
  }

  private open(): Promise<IDBDatabase> {
    return openStickerDb(this.factory);
  }

  async restoreCollection(): Promise<AdapterScanSession | null> {
    const db = await this.open();
    let code: StickerCode | undefined;
    try {
      const records = await getAllCodeRecords(db);
      const saves = await getAllSaveRecords(db);
      const last = await getMeta(db, LAST_COLLECTION_CODE);
      const usable = records.filter(record => record.used && !record.fault &&
        saves.some(save => save.saveId === record.saveId));
      // Older installs have no resume pointer. Select the most recently saved
      // established collection, with a stable numeric-code tie break.
      usable.sort((a, b) => {
        const time = (id: string | null) => saves.find(save => save.saveId === id)?.updatedAt ?? 0;
        return time(b.saveId) - time(a.saveId) || Number(a.code) - Number(b.code);
      });
      code = usable.find(record => record.code === last)?.code ?? usable[0]?.code;
    } finally {
      db.close();
    }
    return code === undefined ? null : this.scan({ preferredCode: code, requireUsed: true });
  }

  async nextUnusedDemoCode(candidates: readonly StickerCode[]): Promise<StickerCode | null> {
    const db = await this.open();
    try {
      const records = await getAllCodeRecords(db);
      return candidates.find(code => FIXTURE_CODES.includes(code) &&
        records.some(record => record.code === code && !record.used && !record.fault)) ?? null;
    } finally {
      db.close();
    }
  }

  async scan(request: AdapterScanRequest): Promise<AdapterScanSession> {
    // preferredCode is an explicit test seam. Normal scans always obtain the
    // physical code from the live camera/compute controller; IndexedDB only
    // resolves that code to the demo project, tags, activation state and save.
    let code: StickerCode;
    if (request.preferredCode !== undefined) {
      await delay(this.scanDelayMs, request.signal);
      code = request.preferredCode;
    } else {
      if (!this.controller) {
        throw new StickerError(
          'unsupported',
          'Live sticker scanning is not available.'
        );
      }
      let acceptedCodes: StickerCode[] | undefined;
      if (request.requireUsed) {
        const db = await this.open();
        try {
          const [records, saves] = await Promise.all([getAllCodeRecords(db), getAllSaveRecords(db)]);
          acceptedCodes = records.filter(record => record.used && !record.fault &&
            record.code !== request.excludedCode &&
            saves.some(save => save.saveId === record.saveId)).map(record => record.code);
        } finally {
          db.close();
        }
        if (!acceptedCodes.length) {
          throw new StickerError('collection_conflict',
            'No committed collection is available on this browser. Cancel adding, scan your first robot, and choose Start new collection. Existing saves have not been changed.');
        }
      }
      code = await this.controller.scan({ ...request, acceptedCodes });
    }

    const db = await this.open();
    try {
      if (request.excludedCode === code) {
        throw new StickerError(
          'same_sticker',
          'Scan a different robot that is already in the collection.'
        );
      }

      let codeRecord = await getCodeRecord(db, code);
      // Proof scans are read-only until a valid established member is found.
      // Keep this check even with a live filter: adapters can be mocked and a
      // different tab can change ownership while the camera is open.
      if (request.requireUsed && !codeRecord?.used) {
        throw new StickerError(
          'collection_conflict',
          `Read sticker ${code}, but it is not committed to a collection. Scan a robot that already belongs to the existing collection.`
        );
      }
      if (!codeRecord) {
        // Unknown code (arbitrary preferredCode or dev-added): provision an
        // independent code record so it behaves like a fresh sticker.
        codeRecord = {
          code,
          saveId: saveIdForCode(code),
          used: false,
          fault: false,
          tagId: DEFAULT_TAG_ID,
        };
        await putCodeRecord(db, codeRecord);
      }

      if (codeRecord.fault) {
        throw new StickerError('scan_failed', 'Simulated scan failure');
      }

      const isNewCode = !codeRecord.used;

      const saveId = codeRecord.saveId ?? saveIdForCode(code);

      // Establish the save on first activation; retrieve it on later scans.
      let saveRecord = await getSaveRecord(db, saveId);
      const now = Date.now();
      if (!saveRecord) {
        saveRecord = {
          saveId,
          data: seedSaveData(code),
          createdAt: now,
          updatedAt: now,
        };
        await putSaveRecord(db, saveRecord);
      }

      if (codeRecord.saveId !== saveId) {
        codeRecord = { ...codeRecord, saveId };
        await putCodeRecord(db, codeRecord);
      }

      const allCodes = await getAllCodeRecords(db);
      const linkedCodes = linkedCodesFrom(allCodes, code);

      // Resolve the code's batch/project tag to a complete {id,name}.
      const allTags = await getAllTagRecords(db);
      const tag = resolveTag(allTags, codeRecord.tagId);
      const members = allCodes
        .filter((record) => record.saveId === saveId)
        .map((record) => ({
          code: record.code,
          tag: resolveTag(allTags, record.tagId),
        }));

      const sessionId = `idb-${++this.sessionCounter}-${now}`;
      this.sessions.set(sessionId, { code, saveId, isNewCode });

      // A first scan remains activated but uncommitted until the service either
      // starts a collection or links it to an established one.
      const status: StickerCodeStatus = isNewCode ? 'activated' : 'used';
      if (!isNewCode) await setMeta(db, LAST_COLLECTION_CODE, code);

      return {
        sessionId,
        code,
        saveGameId: saveId,
        status,
        tag,
        linkedCodes,
        members,
        save: deepCloneJson(saveRecord.data),
        isNewCode,
      };
    } finally {
      db.close();
    }
  }

  async readSave(sessionId: string): Promise<JsonObject> {
    const session = this.requireSession(sessionId);
    const db = await this.open();
    try {
      const record = await getSaveRecord(db, session.saveId);
      if (!record) {
        throw new StickerError('internal', `Missing save: ${session.saveId}`);
      }
      return deepCloneJson(record.data);
    } finally {
      db.close();
    }
  }

  async writeSave(sessionId: string, data: JsonObject): Promise<void> {
    const session = this.requireSession(sessionId);
    const db = await this.open();
    try {
      const existing = await getSaveRecord(db, session.saveId);
      const now = Date.now();
      await putSaveRecord(db, {
        saveId: session.saveId,
        data: deepCloneJson(data),
        createdAt: existing?.createdAt ?? now,
        updatedAt: now,
      });
    } finally {
      db.close();
    }
  }

  async getLinkedCodes(sessionId: string): Promise<StickerCode[]> {
    const session = this.requireSession(sessionId);
    const db = await this.open();
    try {
      const allCodes = await getAllCodeRecords(db);
      return linkedCodesFrom(allCodes, session.code);
    } finally {
      db.close();
    }
  }

  async commitNewCollection(sessionId: string): Promise<AdapterScanSession> {
    const session = this.requireSession(sessionId);
    const db = await this.open();
    try {
      const codeRecord = await getCodeRecord(db, session.code);
      if (!codeRecord) {
        throw new StickerError('internal', `Unknown sticker code: ${session.code}`);
      }
      await putCodeRecord(db, { ...codeRecord, used: true });
      await setMeta(db, LAST_COLLECTION_CODE, session.code);
    } finally {
      db.close();
    }
    session.isNewCode = false;
    return this.snapshotSession(sessionId);
  }

  async linkNewCodeToExistingCode(
    newSessionId: string,
    existingSessionId: string
  ): Promise<AdapterScanSession> {
    const source = this.requireSession(newSessionId);
    const target = this.requireSession(existingSessionId);

    if (!source.isNewCode) {
      throw new StickerError(
        'collection_conflict',
        'This robot already belongs to a collection.'
      );
    }
    if (target.isNewCode) {
      throw new StickerError(
        'collection_conflict',
        'Scan a robot that already belongs to the existing collection.'
      );
    }

    const db = await this.open();
    try {
      await linkNewCodeToExistingSave(
        db,
        source.code,
        source.saveId,
        target.code
      );
      source.saveId = target.saveId;
      source.isNewCode = false;
      await setMeta(db, LAST_COLLECTION_CODE, target.code);
    } finally {
      db.close();
    }
    return this.snapshotSession(existingSessionId);
  }

  async endSession(sessionId: string): Promise<void> {
    // Runtime-only: clears the session mapping. Persistent save/code data is
    // intentionally preserved.
    this.sessions.delete(sessionId);
  }

  demoFixtures(): StickerCode[] {
    return [...FIXTURE_CODES];
  }

  private requireSession(sessionId: string): ActiveSession {
    const session = this.sessions.get(sessionId);
    if (!session) {
      throw new StickerError('not_scanning', `Unknown session: ${sessionId}`);
    }
    return session;
  }

  private async snapshotSession(
    sessionId: string
  ): Promise<AdapterScanSession> {
    const session = this.requireSession(sessionId);
    const db = await this.open();
    try {
      const [codeRecord, saveRecord, allCodes, allTags] = await Promise.all([
        getCodeRecord(db, session.code),
        getSaveRecord(db, session.saveId),
        getAllCodeRecords(db),
        getAllTagRecords(db),
      ]);
      if (!codeRecord || !saveRecord) {
        throw new StickerError('internal', 'Linked collection data is missing.');
      }
      const linkedCodes = linkedCodesFrom(allCodes, session.code);
      return {
        sessionId,
        code: session.code,
        saveGameId: session.saveId,
        status: 'used',
        tag: resolveTag(allTags, codeRecord.tagId),
        linkedCodes,
        members: allCodes
          .filter((record) => record.saveId === session.saveId)
          .map((record) => ({
            code: record.code,
            tag: resolveTag(allTags, record.tagId),
          })),
        save: deepCloneJson(saveRecord.data),
        isNewCode: session.isNewCode,
      };
    } finally {
      db.close();
    }
  }
}

export type { CodeRecord };
